import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { LtiPlacement } from '@ublend-npm/aula-schema';
import showToast from '@core/toasts/operations/showToast.operation';
import { getCurrentSpaceId } from '@core/selectors/space';
import uuid from '@core/utils/uuid';
import fetchDeepLinkReturn from '@core/api/lti/fetchDeepLinkReturn';
import saveLti1p3Assignment from '@core/api/lti/saveLti1p3Assignment';
import { buildAssignmentFromContentItem } from './AssignmentDeepLinkingModal';

/**
 * When an LTI tool returns to a top-level /lti/1p3/deep-link page (orphan),
 * the platform redirects back to Aula with ?lti_dl_return=<token>. This hook
 * consumes that token and creates the assignment (Assignments placement only).
 */
export const useConsumeLtiDeepLinkReturn = ({
  onCreated,
}: {
  onCreated?: () => void;
} = {}) => {
  const dispatch = useDispatch();
  const spaceId = useSelector(getCurrentSpaceId);
  const handledRef = useRef(false);

  useEffect(() => {
    if (handledRef.current || typeof window === 'undefined') {
      return;
    }

    const params = new URLSearchParams(window.location.search);
    const token = params.get('lti_dl_return');
    if (!token) {
      return;
    }

    handledRef.current = true;

    const clearParam = () => {
      params.delete('lti_dl_return');
      const next = params.toString();
      const url = `${window.location.pathname}${next ? `?${next}` : ''}${
        window.location.hash
      }`;
      window.history.replaceState({}, '', url);
    };

    (async () => {
      try {
        const payload = await fetchDeepLinkReturn(token);
        clearParam();

        if (
          payload.placement &&
          payload.placement !== LtiPlacement.ASSIGNMENTS &&
          payload.placement !== 'assignments'
        ) {
          dispatch(
            showToast({
              message:
                'Content selection finished. Open Materials and complete selection in the in-app window if it did not update.',
            })
          );
          return;
        }

        const contentItem = payload.contentItems?.[0];
        const targetSpaceId = payload.spaceId || spaceId;
        if (!contentItem || !targetSpaceId || !payload.providerId) {
          throw new Error('Incomplete deep link return payload');
        }

        await saveLti1p3Assignment(
          targetSpaceId,
          buildAssignmentFromContentItem({
            contentItem,
            providerId: payload.providerId,
            resourceLinkId: payload.resourceLinkId || uuid(),
          })
        );

        dispatch(
          showToast({
            message: 'Assignment created from the tool selection.',
          })
        );
        onCreated?.();
      } catch {
        clearParam();
        dispatch(
          showToast({
            message:
              'Could not finish creating the assignment from the tool return. Try selecting content from the in-app window.',
          })
        );
      }
    })();
  }, [dispatch, onCreated, spaceId]);
};

export default useConsumeLtiDeepLinkReturn;
